import { api } from '../../services/api';
import { AttendanceRecord, UpsertAttendanceRequest } from '../../types/attendance';

/** Narrows the Overtime Ledger. Every field is optional; omitted means "no filter". */
export interface OvertimeLedgerFilters {
    departmentId?: string;
    startDate?: string;
    endDate?: string;
    status?: 'pending' | 'approved' | 'rejected' | '';
    employeeId?: string;
}

// Mirrors the backend TimesheetRow interface — kept in sync manually.
// If the backend interface changes, update this type to match.
export interface TimesheetRow {
    employee_id:   string;
    employee_code: string;
    employee_name: string;
    department:    string | null;
    date:          string;
    status:        string | null;
    shift1_planned_start: string | null;
    shift1_planned_end:   string | null;
    shift1_clock_in:      string | null;
    shift1_clock_out:     string | null;
    shift1_hours:         number | null;
    shift2_planned_start: string | null;
    shift2_planned_end:   string | null;
    shift2_clock_in:      string | null;
    shift2_clock_out:     string | null;
    shift2_hours:         number | null;
    normal_hours:  number | null;
    ot_hours:      number | null;
    total_hours:   number | null;
    late_minutes:  number | null;
    early_leaving_minutes: number | null;
    location:      string | null;
    notes:         string | null;
    method:        string | null;
    is_future:     boolean;
}

export const attendanceApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getAttendance: builder.query<{ data: AttendanceRecord[], totalCount: number, page: number, limit: number }, { employeeId?: string; departmentId?: string; locationId?: string; startDate?: string; endDate?: string, page?: number, limit?: number } | void>({
            query: (params) => ({
                url: '/v1/attendance',
                method: 'GET',
                params,
            }),
            providesTags: (result) =>
                result?.data
                    ? [
                        ...result.data.map(({ id }) => ({ type: 'Attendance' as const, id })),
                        { type: 'Attendance', id: 'LIST' },
                    ]
                    : [{ type: 'Attendance', id: 'LIST' }],
        }),
        getTodayStatus: builder.query<{ success: boolean; data: { isClockedIn: boolean; summary: any; slots: any[] } }, void>({
            query: () => '/v1/attendance/today',
            providesTags: ['Attendance'],
        }),
        getMyGeofence: builder.query<{
            success: boolean;
            data: { branchId: string; branchName: string; latitude: number; longitude: number; radiusMeters: number } | null;
        }, void>({
            query: () => '/v1/ess/attendance/geofence',
        }),
        toggleAttendance: builder.mutation<
            { success: boolean; data: any },
            { deviceInfo: string; sessionId: string; lat?: number; lng?: number; accuracy?: number; reason?: 'manual' | 'geofence_exit' }
        >({
            query: (data) => ({
                url: '/v1/attendance/toggle',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Attendance'],
        }),
        // ─── Admin overrides: reset / resume a day's clock in-out ───────────
        resetAttendance: builder.mutation<
            { success: boolean; message: string },
            { employeeId: string; workDate?: string }
        >({
            query: (data) => ({ url: '/v1/attendance/reset', method: 'POST', data }),
            invalidatesTags: ['Attendance'],
        }),
        resumeClockOut: builder.mutation<
            { success: boolean; message: string; data: AttendanceRecord },
            { employeeId: string; workDate?: string }
        >({
            query: (data) => ({ url: '/v1/attendance/resume', method: 'POST', data }),
            invalidatesTags: ['Attendance'],
        }),
        markAttendance: builder.mutation<{ data: AttendanceRecord }, any>({
            query: (data) => ({
                url: '/v1/attendance/update',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [{ type: 'Attendance', id: 'LIST' }],
        }),
        bulkMarkAttendance: builder.mutation<{ success: boolean; message: string }, any>({
            query: (data) => ({
                url: '/v1/attendance/bulk-mark',
                method: 'POST',
                body: data,
            }),
            // Bulk-marking a range as "On Leave (Type)" bridges to Leave Management on the
            // backend (creates + auto-approves a leave request, updating leave_balances), so
            // leave analytics/balance views must be refetched too, not just attendance lists.
            invalidatesTags: (_result, _error, arg) =>
                arg?.status?.startsWith('On Leave (')
                    ? [{ type: 'Attendance', id: 'LIST' }, 'Leave']
                    : [{ type: 'Attendance', id: 'LIST' }],
        }),
        deleteAttendance: builder.mutation<void, string>({
            query: (id) => ({
                url: `/v1/attendance/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Attendance', id: 'LIST' }],
        }),
        getShifts: builder.query<{ success: boolean; data: any[] }, void>({
            query: () => '/v1/attendance/shifts',
            providesTags: ['Attendance'],
        }),
        createShift: builder.mutation<any, { name: string; start_time: string; end_time: string; overtime_rate_multiplier?: number; department_id?: string | null; color_code?: string }>({
            query: (data) => ({
                url: '/v1/attendance/shifts',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Attendance'],
        }),
        deleteShift: builder.mutation<void, string>({
            query: (id) => ({
                url: `/v1/attendance/shifts/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Attendance'],
        }),
        getOvertimeRules: builder.query<{ success: boolean; data: any[] }, void>({
            query: () => '/v1/attendance/overtime-rules',
            providesTags: ['Attendance'],
        }),
        createOvertimeRule: builder.mutation<any, { shift_id: string | null; threshold_hours: number; perk_description: string }>({
            query: (data) => ({
                url: '/v1/attendance/overtime-rules',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Attendance'],
        }),
        deleteOvertimeRule: builder.mutation<void, string>({
            query: (id) => ({
                url: `/v1/attendance/overtime-rules/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Attendance'],
        }),
        updateOvertimeRule: builder.mutation<any, { id: string; shift_id: string | null; threshold_hours: number; perk_description: string }>({
            query: ({ id, ...body }) => ({
                url: `/v1/attendance/overtime-rules/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Attendance'],
        }),
        getOvertimeRecords: builder.query<{ success: boolean; data: any[] }, OvertimeLedgerFilters | void>({
            query: (filters) => ({
                url: '/v1/attendance/overtime-records',
                method: 'GET',
                // Empty strings would filter on '' and return nothing, so only
                // send the keys that are actually set.
                params: Object.fromEntries(
                    Object.entries(filters || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
                ),
            }),
            providesTags: ['Attendance'],
        }),
        createOvertimeRecord: builder.mutation<any, { employee_id: string; overtime_date: string; hours: number; rate_multiplier: number; shift_id: string | null }>({
            query: (data) => ({
                url: '/v1/attendance/overtime-records',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Attendance', 'Payroll'],
        }),
        approveOvertimeRecord: builder.mutation<any, {
            id: string;
            hourlyRate?: number;
            // Omit (or 'payroll') for the existing pay-it-out behavior. Pass
            // 'comp_leave' + compLeaveTypeId + compLeaveDays to instead credit
            // leave days onto the employee's balance and skip payroll entirely.
            compensationType?: 'payroll' | 'comp_leave';
            compLeaveTypeId?: string;
            compLeaveDays?: number;
        }>({
            query: ({ id, ...body }) => ({
                url: `/v1/attendance/overtime-records/${id}/approve`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Attendance', 'Payroll', 'Leave'],
        }),
        deleteOvertimeRecord: builder.mutation<any, string>({
            query: (id) => ({
                url: `/v1/attendance/overtime-records/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Attendance', 'Payroll'],
        }),
        /**
         * Approve many records at once. Pass `ids` for an explicit selection, or
         * omit it and pass the current filters to approve every pending record
         * that matches — the server re-resolves that set itself so it can't act
         * on a stale client list. Always the payroll path; compensatory-leave
         * approval needs a per-record day figure and stays individual.
         */
        bulkApproveOvertimeRecords: builder.mutation<
            {
                success: boolean;
                message: string;
                data: {
                    approved: number; failed: number; totalAmount: number;
                    failures: Array<{ id: string; employee: string; date: string; reason: string }>;
                };
            },
            { ids?: string[] } & OvertimeLedgerFilters
        >({
            query: (body) => ({
                url: '/v1/attendance/overtime-records/bulk-approve',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Attendance', 'Payroll', 'Leave'],
        }),
        // Catch-up scan: re-runs auto OT detection over a date range for every
        // OT-eligible employee, so the Ledger reflects overtime that's already
        // in attendance data (e.g. CSV-imported/legacy) but never triggered the
        // live clock-out auto-detection.
        syncOvertimeRecords: builder.mutation<{ success: boolean; data: { scanned: number } }, { startDate: string; endDate: string }>({
            query: (body) => ({
                url: '/v1/attendance/overtime-records/sync',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Attendance'],
        }),
        updateShift: builder.mutation<any, { id: string; name: string; start_time: string; end_time: string; overtime_rate_multiplier?: number; department_id?: string | null; color_code?: string }>({
            query: ({ id, ...body }) => ({
                url: `/v1/attendance/shifts/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Attendance'],
        }),
        // ─── Import Attendance ────────────────────────────────────────────────
        // Export is handled as a plain fetch (blob download) in the dashboard component,
        // not RTK Query, because it produces a file stream rather than JSON.
        importAttendance: builder.mutation<
            { success: boolean; message: string; data: { total: number; success: number; failed: number; errors: any[] } },
            FormData
        >({
            query: (formData) => ({
                url: '/v1/attendance/import',
                method: 'POST',
                body: formData,
                // RTK Query / fetchBaseQuery must not set Content-Type — browser sets it
                // automatically with the correct multipart boundary when body is FormData.
                formData: true,
            }),
            invalidatesTags: [{ type: 'Attendance', id: 'LIST' }, { type: 'Attendance', id: 'TIMESHEET' }],
        }),
        // ─── Timesheet Grid (UI) ─────────────────────────────────────────────
        // Returns the same row schema as the CSV export so UI and download are
        // always in parity. Future dates are included with is_future=true.
        getAttendanceTimesheet: builder.query<
            {
                success: boolean;
                data: TimesheetRow[];
                totalEmployees: number;
                page: number;
                limit: number;
                startDate: string;
                endDate: string;
            },
            {
                startDate: string;
                endDate: string;
                employeeId?: string;
                departmentId?: string;
                locationId?: string;
                page?: number;
                limit?: number;
            }
        >({
            query: (params) => ({
                url: '/v1/attendance/timesheet',
                method: 'GET',
                params,
            }),
            providesTags: [{ type: 'Attendance', id: 'TIMESHEET' }],
        }),
    }),
});

export const {
    useGetAttendanceQuery,
    useGetTodayStatusQuery,
    useGetMyGeofenceQuery,
    useToggleAttendanceMutation,
    useResetAttendanceMutation,
    useResumeClockOutMutation,
    useMarkAttendanceMutation,
    useBulkMarkAttendanceMutation,
    useDeleteAttendanceMutation,
    useGetShiftsQuery,
    useCreateShiftMutation,
    useUpdateShiftMutation,
    useDeleteShiftMutation,
    useGetOvertimeRulesQuery,
    useCreateOvertimeRuleMutation,
    useUpdateOvertimeRuleMutation,
    useDeleteOvertimeRuleMutation,
    useGetOvertimeRecordsQuery,
    useCreateOvertimeRecordMutation,
    useApproveOvertimeRecordMutation,
    useBulkApproveOvertimeRecordsMutation,
    useDeleteOvertimeRecordMutation,
    useSyncOvertimeRecordsMutation,
    useImportAttendanceMutation,
    useGetAttendanceTimesheetQuery,
} = attendanceApi;
